
import { supabase } from '@/integrations/supabase/client';
import { CalendarEvent } from '@/pages/scheduling/types';
import { fromLocalISOString } from '@/utils/timezoneUtils';

export const fetchAllGuardSchedules = async (
  guardId: string, 
  dateRange: { start: Date; end: Date }
): Promise<CalendarEvent[]> => {
  try {
    console.log(`[FETCH_SCHEDULES] Fetching schedules for guard ${guardId} from ${dateRange.start.toISOString()} to ${dateRange.end.toISOString()}`);
    
    // Fetch schedules with team information
    const { data: schedules, error } = await supabase
      .from('team_schedules')
      .select(`
        *,
        teams!inner(name)
      `)
      .contains('assigned_guards', [guardId])
      .gte('start_date', dateRange.start.toISOString())
      .lte('start_date', dateRange.end.toISOString())
      .order('start_date', { ascending: true });

    if (error) {
      console.error('[FETCH_SCHEDULES] Error fetching schedules:', error);
      throw error;
    }

    if (!schedules || schedules.length === 0) {
      console.log(`[FETCH_SCHEDULES] No schedules found for guard ${guardId}`);
      return [];
    }

    // Transform the data to include team_name
    const transformedSchedules: CalendarEvent[] = schedules.map(schedule => {
      const teamName = schedule.teams?.name || 'Unknown Team';
      
      console.log(`  - Shift: ${schedule.title}, Start: ${schedule.start_date}, End: ${schedule.end_date}, Team: ${teamName}`);
      
      return {
        ...schedule,
        team_name: teamName,
        color: schedule.assigned_guards && schedule.assigned_guards.length > 0 ? 'primary' : 'secondary'
      };
    });

    console.log(`[FETCH_SCHEDULES] Found ${transformedSchedules.length} shifts for guard ${guardId}`);
    return transformedSchedules;
    
  } catch (error) {
    console.error(`[FETCH_SCHEDULES] Error fetching schedules for guard ${guardId}:`, error);
    throw error;
  }
};

export const checkGuardScheduleConflicts = async (
  guardId: string,
  startDate: Date,
  endDate: Date,
  excludeScheduleId?: string,
  cachedSchedules?: any[]
): Promise<boolean> => {
  try {
    // Validate dates before using them
    if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.error('[CONFLICT_CHECK] Invalid date provided:', { startDate, endDate });
      return false;
    }
    
    console.log(`[CONFLICT_CHECK] Checking conflicts for guard ${guardId} from ${startDate.toISOString()} to ${endDate.toISOString()}`);
    
    // First try to use cached schedules if available (for immediate response)
    if (cachedSchedules && cachedSchedules.length > 0) {
      console.log(`[CONFLICT_CHECK] Using cached schedules (${cachedSchedules.length} total)`);
      
      const conflictingSchedules = cachedSchedules.filter(schedule => {
        // Skip if this is the schedule we're excluding
        if (excludeScheduleId && schedule.id === excludeScheduleId) {
          return false;
        }
        
        // Check if guard is assigned to this schedule
        if (!schedule.assigned_guards || !schedule.assigned_guards.includes(guardId)) {
          return false;
        }
        
        // Check for time overlap
        const scheduleStart = fromLocalISOString(schedule.start_date);
        const scheduleEnd = fromLocalISOString(schedule.end_date);
        
        const hasOverlap = startDate < scheduleEnd && endDate > scheduleStart;
        
        if (hasOverlap) {
          console.log(`[CONFLICT_CHECK] Cache conflict found: ${schedule.title} (${schedule.start_date} - ${schedule.end_date})`);
        }
        
        return hasOverlap;
      });
      
      const hasConflicts = conflictingSchedules.length > 0;
      console.log(`[CONFLICT_CHECK] Cache check result: ${hasConflicts ? 'conflicts found' : 'no conflicts'}`);
      return hasConflicts;
    }
    
    // Fallback to database query if no cached data
    console.log('[CONFLICT_CHECK] No cached data available, querying database...');
    let query = supabase
      .from('team_schedules')
      .select('id, start_date, end_date, title')
      .contains('assigned_guards', [guardId])
      .or(`and(start_date.lte.${endDate.toISOString()},end_date.gte.${startDate.toISOString()})`);

    // Exclude the current schedule if editing
    if (excludeScheduleId) {
      query = query.neq('id', excludeScheduleId);
    }

    const { data: conflictingSchedules, error } = await query;

    if (error) {
      console.error('[CONFLICT_CHECK] Error checking conflicts:', error);
      throw error;
    }

    const hasConflicts = conflictingSchedules && conflictingSchedules.length > 0;
    
    if (hasConflicts) {
      console.log(`[CONFLICT_CHECK] Found ${conflictingSchedules.length} conflicting schedules:`, conflictingSchedules);
    } else {
      console.log(`[CONFLICT_CHECK] No conflicts found for guard ${guardId}`);
    }

    return hasConflicts;
    
  } catch (error) {
    console.error(`[CONFLICT_CHECK] Error checking conflicts for guard ${guardId}:`, error);
    throw error;
  }
};
